import {PATIENT_INSURANCE_PRIORITY_CHOICES} from '../../../../../@core/patient_insurance_priority_choices';
import {InsurancePlan} from '../../../../../models/insurance';

export type InsurancePriorityOptions = keyof typeof PATIENT_INSURANCE_PRIORITY_CHOICES;
export type InsuranceType = 'MCO' | 'MEDICARE' | 'MEDICAID';

export class InsurancePriorityHelper {
    /**
     * Get available insurance priority options for a specific insurance type
     */
    static getInsurancePriorityOptions(insuranceType: InsuranceType): {id: string; name: any}[] {
        const exceptions: {[key in InsuranceType]?: InsurancePriorityOptions[]} = {
            MEDICARE: ['SECONDARY'],
        };

        const isException = exceptions[insuranceType] || [];
        return Object.entries(PATIENT_INSURANCE_PRIORITY_CHOICES)
            .filter(([key]) => !isException.includes(key as InsurancePriorityOptions))
            .map(([id, name]) => ({id, name}));
    }

    /**
     * Update insurance priorities when a new primary is selected
     */
    static updatePrimaryInsurance(
        selectedInsurancePriority: {[key in InsuranceType]: InsurancePriorityOptions},
        newPrimaryProvider: InsuranceType
    ): {[key in InsuranceType]: InsurancePriorityOptions} {
        const result = {...selectedInsurancePriority};

        // Find any existing primary provider that's not the new one
        const formerPrimaryProvider = Object.entries(selectedInsurancePriority)
            .find(([key, value]) => key !== newPrimaryProvider && value === 'PRIMARY');

        // Clear the former primary if one exists
        if (formerPrimaryProvider) {
            result[formerPrimaryProvider[0] as InsuranceType] = null;
        }

        return result;
    }

    /**
     * Sorts insurance plans based on priority order:
     * 1. Professional Claims - Primary
     * 2. Professional Claims - Secondary
     * 3. Professional Claims - Other
     * 4. Other coverage types based on priority
     */
    static sortInsurancePlans(plans: InsurancePlan[]): InsurancePlan[] {
        return [...plans].sort((a, b) => {
            const aScore = this.getPlanSortScore(a);
            const bScore = this.getPlanSortScore(b);
            return aScore - bScore;
        });
    }

    /**
     * Calculates a sort score for an insurance plan based on its best plan type.
     * Lower scores have higher priority in sorting.
     */
    private static getPlanSortScore(plan: InsurancePlan): number {
        if (!plan.coverage_types || plan.coverage_types.length === 0) {
            return 999; // Plans with no types go to the end
        }

        // Find the best (lowest score) plan type within this plan
        let bestScore = 999;

        for (const planType of plan.coverage_types) {
            let score = 0;

            // Coverage type scoring - Professional Claims gets priority
            if (planType.coverage_type === 'PROFESSIONAL_CLAIMS') {
                score = 0;
            } else {
                score = 100; // Other coverage types get lower priority
            }

            // Priority scoring within coverage type
            switch (planType.priority) {
                case 'PRIMARY':
                    score += 1;
                    break;
                case 'SECONDARY':
                    score += 2;
                    break;
                default: // null, undefined, or other
                    score += 3;
                    break;
            }

            bestScore = Math.min(bestScore, score);
        }

        return bestScore;
    }
}
